import { CommonServerSideParams } from '@/app/types/CommonServerSideParams';
import { OnlyBrowserPageProps } from '@/layouts/core/types/OnlyBrowserPageProps';
import { SSGPageProps } from '@/layouts/core/types/SSGPageProps';
import DefaultLayout from '@/layouts/public/components/PublicLayout';
import {
  getPublicLayoutStaticPaths,
  getPublicLayoutStaticProps,
} from '@/layouts/public/publicLayoutSSG';
import ContextBuilderManager from '@/modules/contextBuilder/components/ContextBuilderManager';
import { AMPLITUDE_PAGES } from '@/modules/core/amplitude/events';
import { LogEvent } from '@/modules/core/amplitude/types/Amplitude';
import useCustomer from '@/modules/core/data/hooks/useCustomer';
import { AirtableRecord } from '@/modules/core/data/types/AirtableRecord';
import { Campus } from '@/modules/core/data/types/Campus';
import { ContextFiltersSettings } from '@/modules/core/data/types/ContextFiltersSettings';
import { Customer } from '@/modules/core/data/types/Customer';
import { EducationalProgram } from '@/modules/core/data/types/EducationalProgram';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { StudentSolution } from '@/modules/core/data/types/StudentSolution';
import { StudentSolutionContext } from '@/modules/core/data/types/StudentSolutionContext';
import { StudentSolutionsCategory } from '@/modules/core/data/types/StudentSolutionsCategory';
import { encodeJSONToQueryParameter } from '@/modules/core/js/url';
import { createLogger } from '@/modules/core/logging/logger';
import EducationalProgramStepFees from '@/modules/solutions/components/EducationalProgramStepFees';
import OurSolutions from '@/modules/solutions/components/OurSolutions';
import { flattenStepsFromEducationalPrograms } from '@/modules/solutions/educationalProgramStep';
import {
  filterStudentSolutions,
  QUERY_SIMULATION_KEY,
  resolveInitialContext,
} from '@/modules/solutions/solutionsFilters';
import { filterExpiredSolutions } from '@/modules/solutions/studentSolution';
import { serializeStudentSolutionContext } from '@/modules/solutions/studentSolutionContext';
import { resolveDefaultGroupBy } from '@/modules/solutions/studentSolutionGroup';
import { Amplitude } from '@amplitude/react-amplitude';
import { isBrowser } from '@unly/utils';
import cloneDeep from 'lodash.clonedeep';
import orderBy from 'lodash.orderby';
import reduce from 'lodash.reduce';
import size from 'lodash.size';
import sortBy from 'lodash.sortby';
import {
  GetStaticPaths,
  GetStaticProps,
  NextPage,
} from 'next';
import {
  NextRouter,
  useRouter,
} from 'next/router';
// eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
import React, {
  Fragment,
  useMemo,
  useState,
} from 'react';

const fileLabel = 'pages/[locale]/index';
const logger = createLogger({ // eslint-disable-line no-unused-vars,@typescript-eslint/no-unused-vars
  fileLabel,
});

/**
 * Only executed on the server side at build time
 * Necessary when a page has dynamic routes and uses "getStaticProps"
 */
export const getStaticPaths: GetStaticPaths<CommonServerSideParams> = getPublicLayoutStaticPaths;

/**
 * Only executed on the server side at build time.
 *
 * @return Props (as "SSGPageProps") that will be passed to the Page component, as props
 *
 * @see https://github.com/vercel/next.js/discussions/10949#discussioncomment-6884
 * @see https://nextjs.org/docs/basic-features/data-fetching#getstaticprops-static-generation
 */
export const getStaticProps: GetStaticProps<SSGPageProps, CommonServerSideParams> = getPublicLayoutStaticProps;

/**
 * SSG pages are first rendered by the server (during static bundling)
 * Then, they're rendered by the client, and gain additional props (defined in OnlyBrowserPageProps)
 * Because this last case is the most common (server bundle only happens during development stage), we consider it a default
 * To represent this behaviour, we use the native Partial TS keyword to make all OnlyBrowserPageProps optional
 *
 * Beware props in OnlyBrowserPageProps are not available on the server
 */
type Props = {} & SSGPageProps<Partial<OnlyBrowserPageProps>>;

/**
 * Solutions page
 *
 * Displays student solutions based on set of filters (contextFilters)
 * The filters can be preloaded and are interactive.
 */
const SolutionsPage: NextPage<Props> = (props): JSX.Element => {
  const router: NextRouter = useRouter();
  const customer: Customer = useCustomer();
  const { modTFP } = customer;
  const {
    serviceLabel,
  } = modTFP || {};
  const customerRef: string = process.env.NEXT_PUBLIC_CUSTOMER_REF;
  const allStudentSolutions: AirtableRecord<StudentSolution>[] = useMemo(
    () => sortBy(filterExpiredSolutions(customer?.studentSolutions), 'label'),
    [customer?.studentSolutions],
  );
  const allEducationalPrograms: AirtableRecord<EducationalProgram>[] = useMemo(
    () => orderBy(customer?.educationalPrograms, [
      'displayPriorityScore',
      'label',
    ], [
      'desc',
      'asc',
    ]),
    [customer?.educationalPrograms],
  );
  const allCampuses: AirtableRecord<Campus>[] = useMemo(
    () => sortBy(customer?.campuses, 'label'),
    [customer?.campuses],
  );
  const allStudentSolutionsCategories: AirtableRecord<StudentSolutionsCategory>[] = useMemo(
    () => orderBy(customer?.studentSolutionsCategories, [
      'displayPriorityScore',
      'label',
    ], [
      'desc',
      'asc',
    ]),
    [customer?.studentSolutionsCategories],
  );
  const allEducationalProgramSteps: Array<EducationalProgramStep> = useMemo(
    () => sortBy(flattenStepsFromEducationalPrograms(allEducationalPrograms), 'label'),
    [allEducationalPrograms],
  );
  const [studentSolutionContext, setStudentSolutionsContext] = useState<StudentSolutionContext>(resolveInitialContext(
    router,
    customerRef,
    allCampuses,
    allEducationalPrograms,
    allEducationalProgramSteps,
    allStudentSolutionsCategories,
  ));

  const contextFiltersSettings: ContextFiltersSettings = {
    // Only display campuses filter if there are more than one available filter
    campusFilterEnabled: size(allCampuses) > 1,
    // Only display programs filter if there are more than one program OR if there are more than one step
    educationalProgramFilterEnabled: size(allEducationalPrograms) > 1 || size(allEducationalPrograms?.[0]?.steps) > 0,
    // Only display categories filter if there are more than one category
    categoryFilterEnabled: size(allStudentSolutionsCategories) > 1,
  };
  contextFiltersSettings.enabledFiltersCount = reduce(contextFiltersSettings, (sum: number, value: boolean) => {
    return sum + (value ? 1 : 0); // Convert each boolean to integer of value 1 if true, or 0 if false
  }, 0);

  return (
    <DefaultLayout
      {...props}
      pageName={AMPLITUDE_PAGES.SOLUTIONS_PAGE}
      headProps={{
        seoTitle: serviceLabel,
      }}
    >
      <Amplitude>
        {
          ({ logEvent }: { logEvent: LogEvent }): JSX.Element => {
            return (
              <Fragment>
                <ContextBuilderManager
                  contextFiltersSettings={contextFiltersSettings}
                  context={studentSolutionContext}
                  onContextUpdate={(newContext: StudentSolutionContext): void => {
                    // When the filters are updated in the SolutionsFilters component, we refresh the filters used by the Solutions component
                    setStudentSolutionsContext(newContext);

                    if (isBrowser()) {
                      // Update the url to match the new context (doesn't perform a re-render, useful for the ability to copy/paste the url)
                      // Do not use "router.push()" or it'll slow down the app - See https://github.com/vercel/next.js/issues/9799
                      const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?${QUERY_SIMULATION_KEY}=${encodeJSONToQueryParameter(serializeStudentSolutionContext(newContext))}`;
                      window.history.pushState({ path: newUrl }, '', newUrl);
                    }
                  }}
                  allStudentSolutions={allStudentSolutions}
                  allCampuses={allCampuses}
                  allEducationalPrograms={allEducationalPrograms}
                  allEducationalProgramSteps={allEducationalProgramSteps}
                  allCategories={allStudentSolutionsCategories}
                />

                <EducationalProgramStepFees
                  selectedEducationalProgramSteps={(studentSolutionContext.educationalProgramSteps)} // XXX Was being cloned, but caused too much performance issues
                />

                <OurSolutions
                  contextFiltersSettings={contextFiltersSettings}
                  customer={customer}
                  studentSolutionsFilteredByContext={filterStudentSolutions(allStudentSolutions, studentSolutionContext)}
                  allCampuses={allCampuses}
                  defaultGroupBy={resolveDefaultGroupBy(studentSolutionContext)}
                  context={(studentSolutionContext)} // XXX Was being cloned, but caused too much performance issues
                />
              </Fragment>
            );
          }
        }
      </Amplitude>
    </DefaultLayout>
  );
};

export default (SolutionsPage);
